/**
 * CRM functional-role permission matrix — the single source of truth for what
 * each staff role sees. The role comes from GET /teams/me: ADMIN accounts stay
 * ADMIN; other staff get a role derived from their team's type
 * (SALES / CALL_CENTER / CONTROL / STAFF). A MENTOR or TEACHER account with no
 * team role resolves to MENTOR (AuthContext.resolveStaffRole).
 *
 * `accent` / `accentSoft` — CSS custom-property references (never raw hex)
 *          for the role's colour: `accent` is the readable text/icon tone,
 *          `accentSoft` the matching tinted surface. Both live in
 *          src/index.css DESIGN TOKENS v3 and swap under [data-theme="dark"],
 *          so consumers must pass them straight through to `color` /
 *          `background` (no string-concatenated alpha like `+ '1a'`).
 * `money`  — may see revenue, order amounts and payment analytics.
 * `nav`    — sidebar destinations this role may open ('*' = all).
 * `landing`— where this role is sent after login when no page sent it to /login,
 *          and where `/` leads (postLoginPath, App.js RoleLanding).
 * `labelKey` / `canKeys` / `cannotKeys` — i18n keys (ns/teams.js) for the role
 *          name and the plain-language access summary shown when adding a
 *          member. Render them with t(); the English wording lives in the
 *          dictionary, not here.
 *
 * NOTE on money: set to Admin, Sales and Account Manager. To let another role
 * see money, flip its `money` flag to true here — it's the only place to change.
 * MENTOR (a course owner without a team role) stays false on purpose: the orders
 * and stats endpoints are scoped to the tenant, not to the owner's own courses,
 * so showing money would show every course's revenue. Flip it once the backend
 * scopes those endpoints to the courses the caller owns.
 */
export const STAFF_ROLES = {
  ADMIN: {
    key: 'ADMIN', labelKey: 'teams.roleName.ADMIN', accent: 'var(--color-danger)', accentSoft: 'var(--color-danger-soft)',
    money: true,
    nav: '*',
    landing: '/dashboard',
    canKeys: ['teams.access.fullAccess', 'teams.access.revenueAndAnalytics', 'teams.access.manageStaffTeamsSettings'],
    cannotKeys: [],
  },
  SALES: {
    key: 'SALES', labelKey: 'teams.roleName.SALES', accent: 'var(--color-success)', accentSoft: 'var(--color-success-soft)',
    money: true,
    nav: ['dashboard', 'courses', 'students', 'clients', 'leads', 'reminders', 'sales', 'promocodes', 'inbox', 'support', 'analytics', 'calendar'],
    landing: '/sales',
    canKeys: ['teams.access.revenueOrdersAmounts', 'teams.access.promocodesDiscounts', 'teams.access.salesAnalytics', 'teams.access.studentsCourses'],
    cannotKeys: ['teams.access.manageStaffTeams', 'teams.access.systemSettings'],
  },
  CALL_CENTER: {
    key: 'CALL_CENTER', labelKey: 'teams.roleName.CALL_CENTER', accent: 'var(--color-secondary)', accentSoft: 'var(--color-secondary-soft)',
    money: false,
    nav: ['dashboard', 'leads', 'reminders', 'students', 'inbox', 'support', 'calendar'],
    // The leads whose call is due (overdue included): the call list. Students who have not been
    // seen for a week stay one click away in the Students directory (?activity=inactive7).
    landing: '/leads?due=now',
    canKeys: ['teams.access.studentProgressAtRisk', 'teams.access.messagingFollowUps', 'teams.access.calendar'],
    cannotKeys: ['teams.access.revenueOrdersAmounts', 'teams.access.salesPromocodes', 'teams.access.manageStaffTeams'],
  },
  CONTROL: {
    key: 'CONTROL', labelKey: 'teams.roleName.CONTROL', accent: 'var(--color-primary-accent)', accentSoft: 'var(--color-primary-soft)',
    money: false,
    nav: ['dashboard', 'courses', 'students', 'analytics', 'inbox', 'support', 'calendar'],
    landing: '/dashboard',
    canKeys: ['teams.access.oversight', 'teams.access.nonFinancialAnalytics', 'teams.access.messaging'],
    cannotKeys: ['teams.access.revenueAmounts', 'teams.access.salesPromocodes', 'teams.access.manageStaffTeams'],
  },
  ACCOUNT_MANAGER: {
    key: 'ACCOUNT_MANAGER', labelKey: 'teams.roleName.ACCOUNT_MANAGER', accent: 'var(--color-teal)', accentSoft: 'var(--color-teal-soft)',
    money: true,
    nav: ['dashboard', 'clients', 'leads', 'reminders', 'courses', 'students', 'sales', 'inbox', 'support', 'calendar', 'analytics'],
    landing: '/clients',
    canKeys: ['teams.access.clientOrgs', 'teams.access.revenueOrdersAmounts', 'teams.access.salesAnalytics', 'teams.access.studentsCourses'],
    cannotKeys: ['teams.access.manageStaffTeams', 'teams.access.systemSettings'],
  },
  // A course owner (MENTOR / TEACHER account) with no team role: their own
  // courses and students, no money (see the note above), no staff or client areas.
  MENTOR: {
    key: 'MENTOR', labelKey: 'teams.roleName.MENTOR', accent: 'var(--color-info)', accentSoft: 'var(--color-info-soft)',
    money: false,
    nav: ['dashboard', 'courses', 'students', 'analytics', 'inbox', 'support', 'calendar'],
    landing: '/dashboard',
    canKeys: ['teams.access.ownCourses', 'teams.access.studentsProgress', 'teams.access.nonFinancialAnalytics', 'teams.access.messaging', 'teams.access.calendar'],
    cannotKeys: ['teams.access.revenueOrdersAmounts', 'teams.access.salesPromocodes', 'teams.access.manageStaffTeams'],
  },
  STAFF: {
    key: 'STAFF', labelKey: 'teams.roleName.STAFF', accent: 'var(--color-text-muted)', accentSoft: 'var(--color-surface-muted)',
    money: false,
    nav: ['dashboard', 'students', 'inbox', 'support'],
    landing: '/dashboard',
    canKeys: ['teams.access.students', 'teams.access.messaging'],
    cannotKeys: ['teams.access.revenueAmounts', 'teams.access.sales', 'teams.access.manageStaffTeams'],
  },
};

/** Team type → the functional role a member of that team gets. */
export const TEAM_TYPE_TO_ROLE = {
  CALL_CENTER: 'CALL_CENTER',
  SALES: 'SALES',
  CONTROL: 'CONTROL',
  ACCOUNT_MGMT: 'ACCOUNT_MANAGER',
  GENERAL: 'STAFF',
};

export function roleConfig(role) {
  return STAFF_ROLES[String(role || '').toUpperCase()] || STAFF_ROLES.STAFF;
}

export function roleCanSeeNav(role, navKey) {
  const c = roleConfig(role);
  return c.nav === '*' || c.nav.includes(navKey);
}

export function roleCanSeeMoney(role) {
  return !!roleConfig(role).money;
}

/** Account roles that work from the teacher area (/teacher/*), whatever their team. */
const TEACHER_AREA_ACCOUNTS = ['TEACHER', 'MENTOR'];

/**
 * Where to go after signing in. TEACHER and MENTOR accounts go to the teacher dashboard, as
 * before; anyone else goes back to the page that sent them to /login (`returnTo`, a path with its
 * query), and with no such page (or only `/`) to their role's `landing`.
 */
export function postLoginPath({ returnTo, accountRole, staffRole }) {
  if (TEACHER_AREA_ACCOUNTS.includes(String(accountRole || '').toUpperCase())) {
    return '/teacher/dashboard';
  }
  if (returnTo && returnTo !== '/') return returnTo;
  return roleConfig(staffRole).landing;
}
